import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SITE from '../api';
import { useSite, IMG } from '../components/StoreLayout';
import useSEO from '../useSEO';
import { STORE_BASE } from '../routeBase';

// Same googtrans-cookie mechanism the header's language switcher uses — set
// the cookie to the entity's local language and reload once, so the whole
// page (including the header/footer) renders translated automatically.
// Also handles the reverse: if a previous entity page left the cookie set to
// e.g. French and this entity's language is English, it clears the cookie
// back to English rather than leaving the stale language showing.
function ensureLanguage(targetLang) {
  const wanted = targetLang || 'en';
  const current = (document.cookie.match(/googtrans=\/en\/([a-zA-Z-]+)/) || [])[1] || 'en';
  if (current === wanted) return;
  if (sessionStorage.getItem('sf_entity_lang_set') === wanted) return; // already tried this session — avoid a reload loop
  sessionStorage.setItem('sf_entity_lang_set', wanted);
  const domain = window.location.hostname;
  if (wanted === 'en') {
    document.cookie = 'googtrans=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
    document.cookie = `googtrans=; path=/; domain=${domain}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  } else {
    document.cookie = `googtrans=/en/${wanted}; path=/`;
    document.cookie = `googtrans=/en/${wanted}; path=/; domain=${domain}`;
  }
  window.location.reload();
}

export default function EntityLanding() {
  const { code } = useParams();
  const { content: c } = useSite();
  const [entity, setEntity] = useState(null);
  const [notFound, setNotFound] = useState(false);
  // Live count of countries we actually serve — the same "World Users" data
  // already used in the footer map and Contact Us page — never a fixed
  // number in code, since that goes stale the moment a new customer signs up.
  const [countryCount, setCountryCount] = useState(null);

  useEffect(() => {
    setEntity(null); setNotFound(false);
    SITE.get(`/entity/${encodeURIComponent(code)}`)
      .then(r => { setEntity(r.data); ensureLanguage(r.data.language); })
      .catch(() => setNotFound(true));
    SITE.get('/world-users').then(r => setCountryCount((r.data.countries || []).length)).catch(() => {});
  }, [code]);

  useSEO({
    title: entity ? `${entity.short_name || entity.name} — Wholesale Eyewear` : undefined,
    description: entity ? `${c['brand.name'] || 'Prakaash Eyewear'}'s official representative in ${entity.country} — wholesale eyeglass frames, sunglasses and accessories for opticians and distributors.` : undefined,
    canonical: `${window.location.origin}/store/entity/${code}`,
  });

  if (notFound) return (
    <div style={{ padding: 80, textAlign: 'center', color: '#888' }}>
      Office not found. <Link to={`${STORE_BASE}/contact`} style={{ color: 'var(--sf-accent)' }}>View all offices</Link>
    </div>
  );
  if (!entity) return <div style={{ padding: 80, textAlign: 'center', color: '#aaa' }}>Loading…</div>;

  const banner = entity.hero_image || c['homepage.hero_banner'];

  return (
    <div>
      {/* hero */}
      <div style={{ position: 'relative', background: banner ? '#000' : 'var(--sf-primary,#191919)', minHeight: 320, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
        {banner && <img src={IMG(banner)} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.45 }} />}
        <div style={{ position: 'relative', maxWidth: 900, margin: '0 auto', padding: '60px 24px', textAlign: 'center', color: '#fff' }}>
          {/* An actual flag image, not the flag_emoji text — Windows desktop
              browsers have no flag-glyph emoji font and render regional-
              indicator flag emoji as a literal two-letter code (e.g. "LK")
              instead of a flag, while Android's Noto Color Emoji renders it
              fine. An <img> looks identical on every OS. */}
          {entity.country_code ? (
            <img
              src={`https://flagcdn.com/h60/${entity.country_code.toLowerCase()}.png`}
              alt={entity.country}
              style={{ height: 40, borderRadius: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.35)', marginBottom: 10 }}
            />
          ) : (
            <div style={{ fontSize: 44, marginBottom: 10 }}>{entity.flag_emoji}</div>
          )}
          <h1 style={{ fontSize: 32, fontWeight: 700, marginBottom: 8 }}>Welcome to {entity.short_name || entity.name}</h1>
          <p style={{ fontSize: 15, opacity: 0.9, maxWidth: 640, margin: '0 auto' }}>
            {c['brand.name'] || 'Prakaash Eyewear'}'s official representative in {entity.country} —
            {' '}part of a wholesale eyewear manufacturing and export group{countryCount ? ` serving ${countryCount}+ countries worldwide.` : '.'}
          </p>
        </div>
      </div>

      {/* content */}
      <div style={{ maxWidth: 1000, margin: '-40px auto 0', padding: '0 24px 60px', position: 'relative' }}>
        <div style={{ background: '#fff', borderRadius: 14, boxShadow: '0 8px 30px rgba(0,0,0,0.08)', padding: 32, display: 'flex', gap: 30, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 320px' }}>
            <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 12 }}>About {entity.name}</h2>
            {entity.about_content ? (
              <div className="sf-richtext" style={{ fontSize: 14.5, lineHeight: 1.8, color: '#555' }} dangerouslySetInnerHTML={{ __html: entity.about_content }} />
            ) : (
              <p style={{ fontSize: 14.5, lineHeight: 1.8, color: '#555' }}>
                {c['about.body'] ? stripHtml(c['about.body']) : `${c['brand.name'] || 'Prakaash Eyewear'} designs, manufactures and exports optical frames, sunglasses and accessories to opticians and distributors across the globe. ${entity.name} is the local entity serving customers in ${entity.country} with the same quality, pricing tiers and low minimum order quantities as our global catalogue.`}
              </p>
            )}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 20 }}>
              <Link to={`${STORE_BASE}/shop`} className="sf-btn-outline" style={{ border: '1.5px solid var(--sf-accent)', padding: '11px 24px', borderRadius: 24, fontSize: 13.5, fontWeight: 600, textDecoration: 'none' }}>
                <i className="ti ti-eyeglass-2" /> Browse Products
              </Link>
              <Link to={`${STORE_BASE}/about`} className="sf-btn-outline" style={{ border: '1.5px solid var(--sf-accent)', padding: '11px 24px', borderRadius: 24, fontSize: 13.5, fontWeight: 600, textDecoration: 'none' }}>
                About Our Company
              </Link>
              <Link to={`${STORE_BASE}/contact`} className="sf-btn-outline" style={{ border: '1.5px solid var(--sf-accent)', padding: '11px 24px', borderRadius: 24, fontSize: 13.5, fontWeight: 600, textDecoration: 'none' }}>
                Contact Us
              </Link>
            </div>
          </div>

          <div style={{ flex: '1 1 260px', background: '#fafafa', borderRadius: 12, padding: 22 }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, color: '#555', marginBottom: 14 }}>Office Details</div>
            {entity.address && <InfoRow icon="ti-map-pin" text={entity.address} />}
            {entity.phone && <InfoRow icon="ti-phone" text={entity.phone} href={`tel:${entity.phone}`} />}
            {entity.email && <InfoRow icon="ti-mail" text={entity.email} href={`mailto:${entity.email}`} />}
            {entity.working_hours && <InfoRow icon="ti-clock" text={entity.working_hours} />}
            {entity.google_maps_embed_url && (
              <iframe title="map" src={entity.google_maps_embed_url} style={{ width: '100%', height: 160, border: 'none', borderRadius: 10, marginTop: 14 }} loading="lazy" />
            )}
          </div>
        </div>

        <p style={{ textAlign: 'center', fontSize: 12.5, color: '#999', marginTop: 24 }}>
          Continuing to any link above takes you to our full website — {c['brand.name'] || 'Prakaash Eyewear'}.
        </p>
      </div>
    </div>
  );
}

function InfoRow({ icon, text, href }) {
  const inner = (
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 12, fontSize: 13.5, color: '#444' }}>
      <i className={`ti ${icon}`} style={{ color: 'var(--sf-accent)', fontSize: 15, marginTop: 1 }} />
      <span style={{ whiteSpace: 'pre-line' }}>{text}</span>
    </div>
  );
  return href ? <a href={href} style={{ textDecoration: 'none', color: 'inherit' }}>{inner}</a> : inner;
}

function stripHtml(html) {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
}
